import React from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Appointment } from '../../services/appointmentApi';
import { EmployeeColumn } from './EmployeeColumn';
import './TimelineGrid.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ScheduleException {
  id: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
}

interface TimeSlot {
  hour: number;
  minute: number;
  time: string;
}

interface TimelineGridProps {
  date: Dayjs;
  appointments: Appointment[];
  employees: Employee[];
  schedules: Map<number, TechnicianSchedule[]>;
  exceptions: ScheduleException[];
  onAppointmentClick: (appointment: Appointment) => void;
  onRefresh: () => void;
}

export const TimelineGrid: React.FC<TimelineGridProps> = ({
  date,
  appointments,
  employees,
  schedules,
  exceptions,
  onAppointmentClick,
  onRefresh
}) => {
  // Generate time slots from 7 AM to 7 PM in 15-minute intervals
  const generateTimeSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 7; hour <= 19; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({
          hour,
          minute,
          time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get current time indicator position
  const getCurrentTimePosition = (): number | null => {
    const now = dayjs();
    if (!now.isSame(date, 'day')) return null;

    const currentHour = now.hour();
    const currentMinute = now.minute();

    if (currentHour < 7 || currentHour >= 19) return null;

    // Calculate position as percentage (7 AM = 0%, 7 PM = 100%)
    const totalMinutes = (19 - 7) * 60; // 12 hours = 720 minutes
    const minutesSince7AM = (currentHour - 7) * 60 + currentMinute;
    return (minutesSince7AM / totalMinutes) * 100;
  };

  const currentTimePosition = getCurrentTimePosition();

  return (
    <div className="timeline-grid-container">
      {/* Time axis (left side) */}
      <div className="timeline-time-axis">
        <div className="timeline-header-spacer">Time</div>
        <div className="timeline-time-slots">
          {timeSlots.map((slot, index) => (
            <div
              key={index}
              className={`timeline-time-slot ${slot.minute === 0 ? 'hour-mark' : ''}`}
            >
              {slot.minute === 0 && (
                <span className="time-label">
                  {dayjs().hour(slot.hour).minute(0).format('h:mm A')}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Employee columns */}
      <div className="timeline-employee-columns">
        {employees.map(employee => {
          // Filter appointments for this employee
          const employeeAppointments = appointments.filter(apt => {
            // Check if employee is primary technician
            if (apt.technician_id === employee.id) return true;

            // Check if employee is in multi-tech assignments
            if (apt.technicians?.some(t => t.technician_id === employee.id)) return true;

            return false;
          });

          // Get schedule and exceptions for this employee
          const employeeSchedule = schedules.get(employee.id) || [];
          const employeeExceptions = exceptions.filter(exc => exc.user_id === employee.id);

          return (
            <EmployeeColumn
              key={employee.id}
              employee={employee}
              date={date}
              appointments={employeeAppointments}
              timeSlots={timeSlots}
              schedule={employeeSchedule}
              exceptions={employeeExceptions}
              onAppointmentClick={onAppointmentClick}
            />
          );
        })}
      </div>

      {/* Current time indicator */}
      {currentTimePosition !== null && (
        <div
          className="timeline-current-time-indicator"
          style={{ top: `${currentTimePosition}%` }}
        >
          <div className="current-time-line" />
          <div className="current-time-dot" />
        </div>
      )}
    </div>
  );
};
